import React, { useState } from 'react';
import { Box, Typography, IconButton, Collapse, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import JsonViewer from './JsonViewer';
import { normalizeTelemetryRecord } from '../utils/telemetryRecordNormalizer';

/**
 * TelemetryRow component displays a single telemetry record with
 * expandable/collapsible JSON data and real-time visual indicators.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.telemetry - Telemetry data object
 * @param {boolean} props.isRealTime - Whether this is real-time data
 * @param {boolean} props.isNew - Whether this is newly received data
 * @param {number} props.index - Index of the telemetry record
 * @returns {JSX.Element} - TelemetryRow component
 */
const TelemetryRow = ({ telemetry, isRealTime = false, isNew = false, index = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  const record = normalizeTelemetryRecord(telemetry) || telemetry || {};
  const rawTimestamp = record.timestamp || record.observedAt || record.observed_at;
  
  // Format timestamp for display
  const formattedTimestamp = rawTimestamp
    ? new Date(rawTimestamp).toLocaleString()
    : 'Unknown';
  
  // Handle device ID field name variations
  const deviceId = record.device_id || record.deviceId || record.canopy_id || record.canopyId || 'Unknown';
  
  // Determine row styling based on real-time status
  const getRowStyle = () => {
    const baseStyle = {
      p: 2, 
      borderBottom: '1px solid #e0e0e0',
      transition: 'all 0.3s ease'
    };
    
    if (isNew) {
      return {
        ...baseStyle,
        backgroundColor: '#e8f5e8',
        borderLeft: '4px solid #4caf50',
        animation: 'fadeIn 0.5s ease-in'
      };
    }
    
    if (isRealTime) {
      return {
        ...baseStyle,
        backgroundColor: '#f8f9fa',
        borderLeft: '4px solid #2196f3'
      };
    }
    
    return baseStyle;
  };
  
  return (
    <Box sx={getRowStyle()}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {formattedTimestamp}
            </Typography>
            {isRealTime && (
              <Chip 
                label="🟢 Real-time" 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            )}
            {isNew && (
              <Chip 
                label="🆕 New" 
                size="small" 
                color="success" 
                variant="filled"
              />
            )}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: isNew ? 'bold' : 'normal' }}>
            Device ID: {deviceId}
          </Typography>
          {record.id && (
            <Typography variant="caption" color="text.secondary">
              ID: {record.id}
            </Typography>
          )}
        </Box>
        <IconButton 
          onClick={() => setExpanded(!expanded)}
          size="small"
          aria-label={expanded ? "Collapse" : "Expand"}
          sx={{ ml: 1 }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ 
          mt: 2, 
          backgroundColor: '#f8f8f8', 
          p: 2, 
          borderRadius: 1,
          border: '1px solid #e0e0e0'
        }}>
          <JsonViewer data={record.data || record.payload || record} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default TelemetryRow;
